import { Card, Popover, Stack, Tooltip, Typography } from "@mui/material"
import { useState } from "react";

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import RestrictedAccess from "../../../components/RestrictedAccess";
import { formatCurrency } from "../../../common/Helper";

const IndicatorCard = (props) => {
	const { data, indicator: indicatorId, compareTo = 'index', height, size = 'md' } = props;

	const [anchorEl, setAnchorEl] = useState(null);
	const handleOpen = (event) => setAnchorEl(event.currentTarget);
	const handleClose = () => setAnchorEl(null);
	const open = Boolean(anchorEl);

	const indicator = data?.find(i => i.id === indicatorId);
	if (!indicator) return null;

	const indexName = indicator.currency === 'BRL' ? 'IBOV' : 'S&P500';

	const formatValues = (value) => {
		if (!value) return '-';
		if (indicator.type === 'numeric' && (isNaN(value) || !value)) return '-';

		switch (indicator.unit) {
			case 'currency': return formatCurrency(value, 'SMALL', (indicator.category === 'FINANCIAL' || indicator.category === 'VALUATION') ? indicator.currency : 'USD');
			case 'bigCurrency': return formatCurrency(value, 'BIG', (indicator.category === 'FINANCIAL' || indicator.category === 'VALUATION') ? indicator.currency : 'USD');
			case 'intNumber': return Math.round(value);
			case 'percent': return parseFloat(value).toFixed(2) + '%';
			case 'number': return parseFloat(value).toFixed(2);
			default: return value;
		}
	};

	const color = () => {
		if (!indicator.comparisonType || !indicator.peers?.[compareTo]) return 'textSecondary';
		return indicator.comparisonType === 'BIGISBETTER'
			? (indicator.value >= indicator.peers[compareTo] ? 'success' : 'error')
			: (indicator.value <= indicator.peers[compareTo] ? 'success' : 'error');
	};

	// 🔧 Definições de tamanho responsivo
	const sizeConfig = {
		xs: { fontSize: '0.80rem', labelSize: '0.75rem', boxFontSize: '0.55rem', padding: 0.5, minHeight: 90 },
		sm: { fontSize: '0.90rem', labelSize: '0.80rem', boxFontSize: '0.60rem', padding: 0.75, minHeight: 100 },
		md: { fontSize: '1.00rem', labelSize: '0.90rem', boxFontSize: '0.65rem', padding: 1, minHeight: 115 },
		lg: { fontSize: '1.15rem', labelSize: '1.00rem', boxFontSize: '0.70rem', padding: 1.25, minHeight: 130 },
		xl: { fontSize: '1.30rem', labelSize: '1.10rem', boxFontSize: '0.75rem', padding: 1.5, minHeight: 150 }
	}[size];

	return (
		<Card
			variant="outlined"
			sx={{
				minHeight: height || sizeConfig.minHeight,
				display: 'flex',
				flexDirection: 'column',
				padding: sizeConfig.padding,
			}}
		>
			<Stack display="flex" height="100%" direction="column" flex={1}>
				<Typography
					variant="h6"
					fontSize={sizeConfig.labelSize}
					display="flex"
					justifyContent="space-between"
				>
					{indicator.label}
					{indicator.description && (
						<Tooltip title={<Typography>{indicator.description}</Typography>}>
							<HelpOutlineIcon fontSize="inherit" />
						</Tooltip>
					)}
				</Typography>

				<Stack direction="row" flex={1} alignItems="center" spacing={1}>
					<Typography
						variant="h6"
						paddingTop={1}
						flex={2}
						color={`${color()}.main`}
						fontWeight={600}
						fontSize={sizeConfig.fontSize}
						>
						{indicator.value === '#'
							? <LockOutlinedIcon onMouseOver={handleOpen} fontSize="inherit" />
							: formatValues(indicator.value)
						}
					</Typography>

					{(['md','lg','xl'].includes(size) && indicator.peers?.industry) ? (
						<Stack border={1} borderRadius={1} borderColor="rgba(0, 0, 0, 0.12)" flex={1}>
							<Typography variant="caption" align="center" fontSize={sizeConfig.boxFontSize}>Segmento</Typography>
							<Typography variant="caption" align="center" fontSize={sizeConfig.boxFontSize} paddingTop={1}>
								{indicator.peers.industry === '#'
									? <LockOutlinedIcon fontSize="inherit" onMouseOver={handleOpen} />
									: formatValues(indicator.peers.industry)
								}
							</Typography>
						</Stack>
					) : null}

					{indicator.peers?.index ? (
						<Stack border={1} borderRadius={1} borderColor="rgba(0, 0, 0, 0.12)" flex={1}>
							<Typography variant="caption" align="center" fontSize={sizeConfig.boxFontSize}>{indexName}</Typography>
							<Typography variant="caption" align="center" fontSize={sizeConfig.boxFontSize} paddingTop={1} >
								{indicator.peers.index === '#'
									? <LockOutlinedIcon fontSize="inherit" onMouseOver={handleOpen} />
									: formatValues(indicator.peers.index)
								}
							</Typography>
						</Stack>
					) : null}
				</Stack>
			</Stack>

			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				transformOrigin={{ vertical: "top", horizontal: "center" }}
				disableRestoreFocus
			>
				<RestrictedAccess />
			</Popover>
		</Card>
	);
};

export default IndicatorCard;